'use client';

import { c as _c } from "react/compiler-runtime";
import { importJobChangeDateLog, type ImportJobChangeDateResult, type JobChangeDateImportRow } from '@/app/_workflows/importJobChangeDateLog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
interface ImportJobChangeDateLogModalProps {
  isOpen: boolean;
  onClose(): void;
}
function parseDate(val: unknown): string | null {
  if (val == null || val === '') return null;

  // XLSX returned a JS Date already (cellDates: true on xlsx files)
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val.toISOString();
  }

  // Excel serial number (e.g. 46127 = Jun 23 2026)
  // 25569 = days between Excel epoch (Dec 30 1899) and Unix epoch (Jan 1 1970)
  if (typeof val === 'number') {
    const d = new Date(Math.round((val - 25569) * 86400 * 1000));
    const year = d.getUTCFullYear();
    return isNaN(d.getTime()) || year < 1900 || year > 2200 ? null : d.toISOString();
  }
  const str = String(val).trim();
  if (!str) return null;
  const d = new Date(str);
  const year = d.getFullYear();
  return isNaN(d.getTime()) || year < 1900 || year > 2200 ? null : d.toISOString();
}
function parseBool(val: unknown): boolean | null {
  if (val == null || val === '') return null;
  if (typeof val === 'boolean') return val;
  const str = String(val).trim().toUpperCase();
  if (str === 'TRUE' || str === '1') return true;
  if (str === 'FALSE' || str === '0') return false;
  return null;
}
function parseRow(row: Record<string, unknown>): JobChangeDateImportRow | null {
  const createdAt = parseDate(row['Created']);
  const site = String(row['Site'] ?? '').trim();
  const requestType = String(row['Request Type'] ?? '').trim();
  if (!createdAt || !site || !requestType) return null;
  return {
    createdAt,
    site,
    requestType,
    customerName: row['Customer'] != null ? String(row['Customer']).trim() || null : null,
    pocName: row['POC Name'] != null ? String(row['POC Name']).trim() || null : null,
    pocEmail: row['POC Email'] != null ? String(row['POC Email']).trim() || null : null,
    quoteNumber: row['Quote Number'] != null ? String(row['Quote Number']).trim() || null : null,
    jobNumber: row['Job Number'] != null ? String(row['Job Number']).trim() || null : null,
    promiseDate: parseDate(row['Promise Date']),
    newPromiseDate: parseDate(row['New Promise Date']),
    recoveryDate: parseDate(row['Recovery Date']),
    reason: row['Reason'] != null ? String(row['Reason']).trim() || null : null,
    partNumber: row['Part Number'] != null ? String(row['Part Number']).trim() || null : null,
    poNumber: row['PO Number'] != null ? String(row['PO Number']).trim() || null : null,
    requesterName: row['Requester Name'] != null ? String(row['Requester Name']).trim() || null : null,
    notification: parseBool(row['Notification'])
  };
}
function parseFile(file: File): Promise<JobChangeDateImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array',
          cellDates: true
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: null
        });

        // Trim column names — CSV headers sometimes have leading spaces (e.g. "ID, Created, ...")
        const rows = rawRows.map(row => Object.fromEntries(Object.entries(row).map(([k, v]) => [k.trim(), v])));
        const parsed: JobChangeDateImportRow[] = rows.map(parseRow).filter((r): r is JobChangeDateImportRow => r !== null);
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
export const ImportJobChangeDateLogModal: React.FC<ImportJobChangeDateLogModalProps> = t0 => {
  const $ = _c(28);
  const {
    isOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rows, setRows] = useState(t1);
  const [parseError, setParseError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t2;
  if ($[1] !== isLoading || $[2] !== onClose) {
    t2 = () => {
      if (isLoading) {
        return;
      }
      setRows([]);
      setParseError(null);
      setImportResult(null);
      onClose();
    };
    $[1] = isLoading;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClose = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async file => {
      setParseError(null);
      setRows([]);
      setImportResult(null);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setParseError("No valid rows found. File must have columns: Created, Site, Request Type.");
          return;
        }
        setRows(parsed);
      } catch {
        setParseError("Failed to parse file. Make sure it is a valid Excel or CSV file.");
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const processFile = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      e.preventDefault();
      setIsDragOver(false);
      const file_0 = e.dataTransfer.files[0];
      if (file_0) {
        processFile(file_0);
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleDrop = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      const file_1 = e_0.target.files?.[0];
      if (file_1) {
        processFile(file_1);
      }
      e_0.target.value = "";
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleFileChange = t5;
  let t6;
  if ($[7] !== doServerAction || $[8] !== rows) {
    t6 = () => {
      doServerAction(async () => ({
        data: await importJobChangeDateLog(rows)
      }), result => {
        if (!result || typeof result !== "object" || !("data" in result)) {
          return;
        }
        setImportResult(result.data);
        setRows([]);
      });
    };
    $[7] = doServerAction;
    $[8] = rows;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleImport = t6;
  const hasIssues = importResult && importResult.errors.length > 0;
  let t7;
  if ($[10] !== handleClose || $[11] !== handleImport || $[12] !== importResult || $[13] !== isLoading || $[14] !== rows) {
    t7 = importResult ? <Button variant="contained" onClick={handleClose}>Close</Button> : <Button variant="contained" disabled={rows.length === 0 || isLoading} onClick={handleImport}>Import {rows.length > 0 ? `(${rows.length} rows)` : ""}</Button>;
    $[10] = handleClose;
    $[11] = handleImport;
    $[12] = importResult;
    $[13] = isLoading;
    $[14] = rows;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== hasIssues || $[17] !== importResult || $[18] !== isDragOver || $[19] !== parseError || $[20] !== rows) {
    t8 = importResult ? <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><CheckCircleOutlineIcon color={hasIssues ? "warning" : "success"} /><Typography variant="body1" fontWeight={500}>Import complete</Typography></Box><Alert severity={hasIssues ? "warning" : "success"}>{importResult.inserted} row{importResult.inserted !== 1 ? "s" : ""} inserted.</Alert>{importResult.errors.length > 0 && <Box><Typography variant="subtitle2" color="error.main" gutterBottom={true}>Errors ({importResult.errors.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{importResult.errors.map(_temp)}</List></Box>}</Box> : <><Box onDrop={handleDrop} onDragOver={e_1 => {
        e_1.preventDefault();
        setIsDragOver(true);
      }} onDragLeave={() => setIsDragOver(false)} onClick={() => fileInputRef.current?.click()} sx={{
        mt: 1,
        mb: 2,
        border: 2,
        borderStyle: "dashed",
        borderColor: isDragOver ? "primary.main" : "divider",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        backgroundColor: isDragOver ? "action.hover" : "background.paper",
        transition: "all 0.15s ease"
      }}><CloudUploadIcon sx={{
          fontSize: 40,
          color: "text.secondary"
        }} /><Typography variant="body2" color="text.secondary">{"Drag & drop or click to select a file"}</Typography><Typography variant="caption" color="text.disabled">Supports .xlsx, .xls, .csv</Typography><input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{
          display: "none"
        }} onChange={handleFileChange} /></Box>{parseError && <Alert severity="error" sx={{
        mb: 2
      }}>{parseError}</Alert>}{rows.length > 0 && <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap"
      }}><Chip label={`${rows.length} rows ready to import`} color="success" size="small" /></Box>}</>;
    $[16] = hasIssues;
    $[17] = importResult;
    $[18] = isDragOver;
    $[19] = parseError;
    $[20] = rows;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== handleClose || $[23] !== isLoading || $[24] !== isOpen || $[25] !== t7 || $[26] !== t8) {
    t9 = <Modal isOpen={isOpen} onClose={handleClose} title="Import Job Change Date Log" subTitle={"Drop a CSV file with columns: ID, Created, Site, Request Type, Customer, Job Number, Promise Date, New Promise Date, Recovery Date\u2026"} maxWidth="sm" fullWidth={true} disableClose={isLoading} isLoading={isLoading} actions={t7}>{t8}</Modal>;
    $[22] = handleClose;
    $[23] = isLoading;
    $[24] = isOpen;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
};
function _temp(id) {
  return <ListItem key={id} disableGutters={true}><ListItemText primary={`ID: ${id}`} primaryTypographyProps={{
      variant: "body2",
      color: "error"
    }} /></ListItem>;
}